import type { ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { EnergyPanel, EnergyPanelDot } from "./EnergyPanel";
import { InsightChart, domainVar, type InsightPoint } from "./InsightChart";
import type { Domain } from "./Tile";

interface EnergyAreaPanelProps {
  /** Header icon (e.g. a lucide icon). */
  icon: ReactNode;
  /** Panel title. */
  title: string;
  /** Single-series legend label (e.g. "Consumption", "Production"). */
  legendLabel: string;
  /** Domain — drives both the chart color and the legend dot. */
  domain: Domain;
  /** Unit suffix shown in the tooltip (e.g. " kWh"). */
  unit?: string;
  /** Chart series. */
  data: InsightPoint[];
  /** Show a skeleton instead of the chart. */
  loading?: boolean;
  /** Text shown when not loading and the series is empty. */
  emptyText?: string;
  className?: string;
}

/**
 * A single-series area chart inside the shared dark {@link EnergyPanel}, with a
 * titled header, a one-item legend, and built-in loading / empty states. This
 * is the design-system building block the Energy tab composes for its
 * "Energy today" / "Solar production" charts so pages pass data, not chrome —
 * a sibling of the multi-series {@link EnergyFlowChart} hero panel.
 */
export function EnergyAreaPanel({
  icon,
  title,
  legendLabel,
  domain,
  unit,
  data,
  loading = false,
  emptyText = "No data available yet.",
  className,
}: EnergyAreaPanelProps) {
  return (
    <EnergyPanel
      icon={icon}
      title={title}
      className={className}
      legend={
        <span className="flex items-center gap-1.5">
          <EnergyPanelDot color={domainVar[domain]} /> {legendLabel}
        </span>
      }
    >
      {loading ? (
        <Skeleton className="h-40 w-full rounded-2xl" />
      ) : data.length > 0 ? (
        <InsightChart data={data} domain={domain} unit={unit} variant="dark" />
      ) : (
        <p className="py-8 text-center text-sm text-zinc-500">{emptyText}</p>
      )}
    </EnergyPanel>
  );
}
